import { useState } from "react";
import api from "@/lib/api";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";

function urlB64ToUint8(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function MatchReminders() {
  const [on, setOn] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!("serviceWorker" in navigator) || typeof Notification === "undefined" || !("PushManager" in window)) {
        toast.error("Reminders aren't supported here", { description: "Install GAFFER to your home screen first." });
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.info("Enable notifications to get match reminders."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.key) });
      }
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setOn(true);
      toast.success("Match reminders on ⚽", { description: "We'll nudge you an hour before your club plays." });
    } catch {
      toast.error("Couldn't enable reminders.");
    } finally { setBusy(false); }
  };

  const test = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(data.sent ? "Test sent — check your notifications." : "Subscribed, but no notification delivered.");
    } catch {
      toast.error("Enable reminders first.");
    }
  };

  return (
    <button
      onClick={on ? test : enable}
      disabled={busy}
      data-testid="match-reminders-btn"
      title={on ? "Match reminders on — click to send a test" : "Enable match reminders"}
      className={`inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-wider transition-colors disabled:opacity-60 ${
        on ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"}`}>
      {on ? <BellRing size={12} /> : <Bell size={12} />}
      <span className="hidden lg:inline">{on ? "Reminders on" : "Reminders"}</span>
    </button>
  );
}
